import { useState, useEffect } from 'react';
import Button from '@/components/Button';
import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import prevMonthIcon from '@/assets/calendar_prev_month.svg';
import prevYearIcon from '@/assets/calendar_prev_year.svg';
import nextMonthIcon from '@/assets/calendar_next_month.svg';
import nextYearIcon from '@/assets/calendar_next_year.svg';
import { formatDateOnly, formatDateWithTimezone } from '@/utils/formatUtils';

const DateRangeFilter = ({ onSearch, onReset, initialRange }) => {
  const { t } = useTranslation();

  // No dates selected by default
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [isOpen, setIsOpen] = useState(false);
  const [currentView, setCurrentView] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth() - 1, 1); // Start with last month
  });

  // Helper functions for date manipulation
  const getMonthStr = (date) => {
    return date.toLocaleString('default', { month: 'short' });
  };

  const parseDateValue = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return parsed;
  };

  useEffect(() => {
    if (!initialRange) {
      return;
    }
    const parsedStart = parseDateValue(initialRange.startDate);
    const parsedEnd = parseDateValue(initialRange.endDate);
    setDateRange([parsedStart, parsedEnd]);
    if (parsedStart) {
      setCurrentView(new Date(parsedStart.getFullYear(), parsedStart.getMonth(), 1));
    }
  }, [initialRange?.startDate, initialRange?.endDate]);

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    // Convert Sunday=0 to Monday=0 format for our grid
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1;
  };

  const isSameDay = (date1, date2) => {
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  };

  const isInRange = (date, start, end) => {
    if (!start || !end) return false;
    return date >= start && date <= end;
  };

  const isToday = (date) => {
    const today = new Date();
    return isSameDay(date, today);
  };

  // Navigation functions
  const goToPreviousMonth = () => {
    setCurrentView(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentView(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const goToPreviousYear = () => {
    setCurrentView(prev => new Date(prev.getFullYear() - 1, prev.getMonth(), 1));
  };

  const goToNextYear = () => {
    setCurrentView(prev => new Date(prev.getFullYear() + 1, prev.getMonth(), 1));
  };

  // Date selection handler
  const handleDateClick = (date) => {
    if (!startDate || (startDate && endDate)) {
      // Start new selection
      setDateRange([date, null]);
    } else if (startDate && !endDate) {
      // Complete the range
      if (date < startDate) {
        setDateRange([date, startDate]);
      } else {
        setDateRange([startDate, date]);
      }
    }
  };

  const handleReset = () => {
    setDateRange([null, null]);
    if (onReset) onReset();
  };

  const handleSearch = () => {
    if (onSearch) {
      const formattedDateRange = {
        startDate: startDate ? formatDateWithTimezone(startDate, false) : null,
        endDate: endDate ? formatDateWithTimezone(endDate, true) : null
      };
      onSearch(formattedDateRange);
    }
  };

  // Render a single month calendar
  const renderMonth = (monthDate, isLeftMonth = true) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);

    // Create array of days for the month
    const days = [];

    // Add days from previous month to fill the beginning of the grid
    for (let i = firstDay - 1; i >= 0; i--) {
      const prevDate = new Date(year, month, -i);
      days.push({ date: prevDate, isCurrentMonth: false });
    }

    // Add days of the current month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push({ date, isCurrentMonth: true });
    }

    // Add days from next month to fill the grid
    const remainingCells = 42 - days.length; // 6 rows × 7 days
    for (let day = 1; day <= remainingCells; day++) {
      const nextDate = new Date(year, month + 1, day);
      days.push({ date: nextDate, isCurrentMonth: false });
    }

    return (
      <div className="w-72 bg-white">
        {/* Header */}
        <div className="flex items-center justify-center py-3 border-b border-gray-200 relative">
          <div className="flex gap-2 text-base font-medium text-gray-900">
            <span>{getMonthStr(monthDate)}</span>
            <span>{year}</span>
          </div>

          {/* Navigation buttons - only show on left month */}
          {isLeftMonth && (
            <div className="absolute left-4 flex gap-1">
              <button
                onClick={goToPreviousYear}
                className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded"
                type="button"
              >
                <img src={prevYearIcon} className="w-4 h-4" alt="Previous year" />
              </button>
              <button
                onClick={goToPreviousMonth}
                className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded"
                type="button"
              >
                <img src={prevMonthIcon} className="w-4 h-4" alt="Previous month" />
              </button>
            </div>
          )}

          {/* Navigation buttons - only show on right month */}
          {!isLeftMonth && (
            <div className="absolute right-4 flex gap-1">
              <button
                onClick={goToNextMonth}
                className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded"
                type="button"
              >
                <img src={nextMonthIcon} className="w-4 h-4" alt="Next month" />
              </button>
              <button
                onClick={goToNextYear}
                className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded"
                type="button"
              >
                <img src={nextYearIcon} className="w-4 h-4" alt="Next year" />
              </button>
            </div>
          )}
        </div>

        {/* Day names */}
        <div className="grid grid-cols-7 border-b border-gray-200">
          {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day) => (
            <div key={day} className="p-2 text-center text-xs text-gray-600 font-normal">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="p-4">
          <div className="grid grid-cols-7 gap-0">
            {days.map(({ date, isCurrentMonth }, index) => {
              // Only apply selection logic to current month dates
              const isRangeStart = isCurrentMonth && startDate && isSameDay(date, startDate);
              const isRangeEnd = isCurrentMonth && endDate && isSameDay(date, endDate);
              const isSelected = isRangeStart || isRangeEnd;
              const isInSelectedRange = isCurrentMonth && isInRange(date, startDate, endDate) && !isSelected;
              const isTodayDate = isToday(date) && isCurrentMonth; // Only highlight today in current month

              return (
                <div key={index} className="relative h-9 flex items-center justify-center">
                  {/* Background for range - only for current month dates */}
                  {isCurrentMonth && (isInSelectedRange || isSelected) && (
                    <div className={`
                      absolute inset-0 bg-gray-100
                      ${isRangeStart ? 'rounded-l' : ''}
                      ${isRangeEnd ? 'rounded-r' : ''}
                    `}></div>
                  )}

                  {isCurrentMonth ? (
                    <button
                      onClick={() => handleDateClick(date)}
                      className={`
                        relative w-9 h-9 flex items-center justify-center text-sm cursor-pointer z-10
                        text-gray-900
                        ${isSelected ? 'bg-gray-900 text-white rounded' : ''}
                        ${isTodayDate && !isSelected && !isInSelectedRange ? 'border border-gray-900 rounded' : ''}
                        ${!isSelected && !isInSelectedRange ? 'hover:bg-gray-200 rounded' : ''}
                      `}
                      type="button"
                    >
                      {date.getDate()}
                    </button>
                  ) : (
                    <div className="relative w-9 h-9 flex items-center justify-center text-sm text-gray-300 z-10">
                      {date.getDate()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const leftMonth = currentView;
  const rightMonth = new Date(currentView.getFullYear(), currentView.getMonth() + 1, 1);

  return (
    <div className="flex items-center gap-4">
      <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
        <Popover.Trigger asChild>
          <div className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-md text-sm cursor-pointer min-w-[240px] bg-white">
            <div className='flex flex-1 justify-center'>
              <span className="text-gray-500">{startDate ? formatDateOnly(startDate) : t('common.startDate')}</span>
              <span className="text-gray-400 px-4">-</span>
              <span className="text-gray-500">{endDate ? formatDateOnly(endDate) : t('common.endDate')}</span>
            </div>
            <div className="">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="bg-white rounded-lg shadow-lg border border-gray-200 z-50 p-0"
            sideOffset={5}
            align="start"
          >
            <div className="flex">
              {renderMonth(leftMonth, true)}
              <div className="w-px bg-gray-200"></div>
              {renderMonth(rightMonth, false)}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      <Button variant="default" size="m" onClick={handleReset}>
        {t('common.reset')}
      </Button>
      <Button variant="primary" size="m" onClick={handleSearch}>
        {t('common.search')}
      </Button>
    </div>
  );
};

export default DateRangeFilter;